import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Download, RefreshCw, Phone, MessageCircle, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authAxios, formatApiError, API } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BRAND } from "@/lib/brand";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const PAGE_SIZES = [25, 50, 100, 200];

export default function AdminDashboard() {
  const nav = useNavigate();
  const { user, checking, logout } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);           // 0-indexed
  const [pageSize, setPageSize] = useState(25);

  // debounce search input
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(0);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const skip = page * pageSize;
      const params = new URLSearchParams({ limit: String(pageSize), skip: String(skip) });
      if (debouncedQ) params.set("q", debouncedQ);
      const [e, s] = await Promise.all([
        authAxios.get(`/admin/enquiries?${params.toString()}`),
        authAxios.get("/admin/stats"),
      ]);
      setItems(e.data.items || []);
      setTotal(e.data.total || 0);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load enquiries");
      if (err.response?.status === 401) nav("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [nav, page, pageSize, debouncedQ]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const doLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("velixa_admin_token");
      const res = await fetch(`${API}/admin/enquiries.csv`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `velixa-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error("CSV download failed. Try again.");
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : page * pageSize + 1;
  const showingTo = Math.min(total, page * pageSize + items.length);

  const gotoPrev = () => setPage((p) => Math.max(0, p - 1));
  const gotoNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));

  if (checking) return <main className="min-h-screen flex items-center justify-center text-ink/60">Checking session…</main>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-bone">
      <div className="border-b border-ink/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-ink/50">Velixa Health · Admin</div>
            <div className="font-serif text-2xl text-ink tracking-editorial">Enquiries</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-back-site" className="hidden md:inline-flex px-4 py-2.5 border border-ink/20 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors">View site</Link>
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 border border-ink/20 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button data-testid="admin-csv" onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2.5 bg-moss text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-moss-2">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-ink-2">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        {stats && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard testid="stat-total" label="Total leads" value={stats.total} />
            <StatCard testid="stat-7d" label="Last 7 days" value={stats.last_7_days} />
            <StatCard testid="stat-24h" label="Last 24 hours" value={stats.last_24_hours} />
            <StatCard testid="stat-top" label="Top service" value={(stats.by_service?.[0]?.service) || "—"} />
          </motion.div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, service, location…" className="pl-9 h-11 rounded-sm bg-white border-ink/15" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.22em] text-ink/50">Per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger data-testid="admin-pagesize" className="h-9 w-[80px] rounded-sm bg-white border-ink/15 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 ml-auto" data-testid="admin-count">
            {loading ? "Loading…" : `Showing ${showingFrom}–${showingTo} of ${total}${debouncedQ ? " · filtered" : ""}`}
          </div>
        </div>

        <div className="bg-white border border-ink/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.24em] text-ink/50 border-b border-ink/10">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="7" className="px-4 py-10 text-center text-ink/50">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-16 text-center text-ink/50" data-testid="admin-empty">No enquiries yet.</td></tr>
                )}
                {!loading && items.map((it, i) => (
                  <tr key={it.id || i} data-testid={`row-${it.id || i}`} className="border-b border-ink/5 hover:bg-cream/40 align-top">
                    <td className="px-4 py-4 text-ink/70 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-ink">{it.name}</div>
                      <div className="text-[12px] text-ink/50">{it.email || ""}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-ink">{it.phone}</td>
                    <td className="px-4 py-4 text-ink/80">{it.service || "—"}</td>
                    <td className="px-4 py-4 text-ink/80">{it.location || "—"}</td>
                    <td className="px-4 py-4 text-ink/70 max-w-[340px]">
                      <div className="whitespace-pre-wrap">{it.message || <span className="text-ink/40">—</span>}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="inline-flex gap-2">
                        <a data-testid={`row-call-${it.id}`} href={`tel:${it.phone}`} title="Call" className="p-2 border border-ink/15 hover:bg-ink hover:text-bone transition-colors"><Phone className="w-3.5 h-3.5" /></a>
                        <a data-testid={`row-wa-${it.id}`} href={`https://wa.me/${(it.phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi " + (it.name || "") + ", thank you for reaching out to Velixa Health.")}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-2 border border-ink/15 hover:bg-moss hover:text-bone transition-colors"><MessageCircle className="w-3.5 h-3.5" /></a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-ink/10 bg-cream/40">
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink/55" data-testid="admin-page-info">
              Page {pageCount === 0 ? 0 : page + 1} of {pageCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="admin-prev"
                onClick={gotoPrev}
                disabled={page === 0 || loading}
                className="inline-flex items-center gap-1 px-3 py-2 border border-ink/15 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                data-testid="admin-next"
                onClick={gotoNext}
                disabled={page >= pageCount - 1 || loading}
                className="inline-flex items-center gap-1 px-3 py-2 border border-ink/15 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink transition-colors"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-[11px] uppercase tracking-[0.22em] text-ink/45">
          Signed in as {user.email} · Support line {BRAND.phone}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-ink/10 p-5">
      <div className="text-[10px] uppercase tracking-[0.28em] text-ink/55">{label}</div>
      <div className="mt-2 font-serif text-4xl text-ink tracking-tightest">{value}</div>
    </div>
  );
}
